import { getContentDisplayLabel } from './src/studioConstants.js'
import contentTypeUtils from './contentTypeUtils.js'
import {
  normalizeDeliveryMethod,
  normalizeDeliverySource,
} from './publishingReliability.js'

const { getContentType, inferContentTypeKey, normalizeIdea } = contentTypeUtils

export const CAPTURE_SOURCE_TYPES = [
  { key: 'build-log', label: 'Build Log' },
  { key: 'system-decision', label: 'System Decision' },
  { key: 'debugging-note', label: 'Debugging Note' },
  { key: 'research-note', label: 'Research Note' },
  { key: 'client-question', label: 'Client Question' },
  { key: 'github-activity', label: 'GitHub Activity' },
  { key: 'raw-idea', label: 'Raw Idea' },
]

export const CAPTURE_SOURCE_TYPE_KEYS = CAPTURE_SOURCE_TYPES.map(
  (type) => type.key
)
export const AUTHORITY_ANGLE_PRESETS = [
  {
    key: 'architecture-tradeoff',
    label: 'Architecture tradeoff',
    defaultPillar: 'system-decision',
  },
  {
    key: 'debugging-boundary',
    label: 'Debugging boundary',
    defaultPillar: 'build-log',
  },
  {
    key: 'evaluation-standard',
    label: 'Evaluation standard',
    defaultPillar: 'engineering-standards',
  },
  {
    key: 'retrieval-quality',
    label: 'Retrieval quality',
    defaultPillar: 'deep-dive',
  },
  {
    key: 'agent-orchestration',
    label: 'Agent orchestration',
    defaultPillar: 'deep-dive',
  },
  {
    key: 'operational-reliability',
    label: 'Operational reliability',
    defaultPillar: 'engineering-standards',
  },
  {
    key: 'founder-risk',
    label: 'Founder risk',
    defaultPillar: 'engineering-standards',
  },
]
export const AUTHORITY_ANGLE_KEYS = AUTHORITY_ANGLE_PRESETS.map(
  (angle) => angle.key
)

export const PERFORMANCE_METRIC_FIELDS = [
  'impressions',
  'likes',
  'replies',
  'saves',
  'profileClicks',
]

const CHECKLIST_PRESETS = {
  'linkedin-post': [
    ['ready', 'Ready'],
    ['copied-or-api-attempted', 'Copied / post API attempted'],
    ['verified-linkedin', 'Verified on LinkedIn'],
  ],
  'x-post': [
    ['thread-reviewed', 'Thread reviewed'],
    ['posted-or-copied', 'Posted / copied'],
    ['verified-x', 'Verified on X'],
  ],
  'linkedin-article': [
    ['exported-markdown', 'Exported markdown'],
    ['pasted-manually', 'Pasted manually'],
    ['verified-url', 'Verified URL'],
  ],
  'newsletter-issue': [
    ['exported-markdown', 'Exported markdown'],
    ['pasted-manually', 'Pasted manually'],
    ['verified-url', 'Verified URL'],
  ],
  'linkedin-carousel': [
    ['slides-reviewed', 'Slides reviewed'],
    ['assets-exported', 'Assets exported'],
    ['uploaded-manually', 'Uploaded manually'],
    ['verified-linkedin', 'Verified on LinkedIn'],
  ],
}

function cleanString(value) {
  return typeof value === 'string' ? value.trim() : ''
}

export function normalizeTruthLinkIds(values = []) {
  const seen = new Set()
  return (Array.isArray(values) ? values : [])
    .map((value) => cleanString(value))
    .filter((value) => {
      if (!value || seen.has(value)) return false
      seen.add(value)
      return true
    })
}

export function normalizeEntityTruthLinks(input = {}, fallback = {}) {
  return {
    projectIds: normalizeTruthLinkIds(
      input?.projectIds ?? fallback.projectIds ?? []
    ),
    positionIds: normalizeTruthLinkIds(
      input?.positionIds ?? fallback.positionIds ?? []
    ),
  }
}

function slugForId(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

export function normalizePerformanceMetric(value) {
  if (value == null) return null
  if (typeof value === 'string' && value.trim() === '') return null
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric < 0) return null
  return Math.floor(numeric)
}

function hasPerformanceSignal(checkIn) {
  return (
    PERFORMANCE_METRIC_FIELDS.some((field) => checkIn[field] != null) ||
    !!checkIn.qualitativeNotes ||
    !!checkIn.taughtMe
  )
}

export function normalizePerformanceFeedbackCheckIn(input = {}, fallback = {}) {
  return {
    id: cleanString(input.id) || cleanString(fallback.id),
    checkedAt: cleanString(input.checkedAt) || cleanString(fallback.checkedAt),
    impressions: normalizePerformanceMetric(input.impressions),
    likes: normalizePerformanceMetric(input.likes),
    replies: normalizePerformanceMetric(input.replies),
    saves: normalizePerformanceMetric(input.saves),
    profileClicks: normalizePerformanceMetric(input.profileClicks),
    qualitativeNotes: cleanString(input.qualitativeNotes),
    taughtMe: cleanString(input.taughtMe),
  }
}

export function normalizePerformanceFeedback(feedback = {}) {
  const checkIns = Array.isArray(feedback?.checkIns)
    ? feedback.checkIns
        .map((checkIn, index) =>
          normalizePerformanceFeedbackCheckIn(checkIn, {
            id: `feedback-${index + 1}`,
          })
        )
        .filter(hasPerformanceSignal)
    : []

  return {
    version: 1,
    checkIns,
  }
}

export function buildPerformanceFeedbackCheckIn(input = {}, checkedAt = null) {
  const finalCheckedAt =
    cleanString(input.checkedAt) ||
    cleanString(checkedAt) ||
    new Date().toISOString()
  const id =
    cleanString(input.id) ||
    `feedback-${Date.parse(finalCheckedAt) || Date.now()}`

  return normalizePerformanceFeedbackCheckIn(
    {
      ...input,
      id,
      checkedAt: finalCheckedAt,
    },
    { id, checkedAt: finalCheckedAt }
  )
}

export function appendPerformanceFeedbackCheckIn(
  existingFeedback = {},
  input = {},
  checkedAt = null
) {
  const feedback = normalizePerformanceFeedback(existingFeedback)
  const checkIn = buildPerformanceFeedbackCheckIn(input, checkedAt)

  if (!hasPerformanceSignal(checkIn)) {
    return feedback
  }

  return {
    version: 1,
    checkIns: [checkIn, ...feedback.checkIns],
  }
}

export function hasPerformanceFeedback(feedback = {}) {
  return normalizePerformanceFeedback(feedback).checkIns.length > 0
}

export function getLatestPerformanceFeedbackCheckIn(feedback = {}) {
  const checkIns = normalizePerformanceFeedback(feedback).checkIns
  if (!checkIns.length) return null

  return [...checkIns].sort(
    (a, b) => new Date(b.checkedAt || 0) - new Date(a.checkedAt || 0)
  )[0]
}

export function getCaptureSourceTypeLabel(sourceType) {
  return (
    CAPTURE_SOURCE_TYPES.find((type) => type.key === sourceType)?.label ||
    'Raw Idea'
  )
}

export function normalizeAuthorityAngle(value) {
  return AUTHORITY_ANGLE_KEYS.includes(value) ? value : ''
}

export function getAuthorityAnglePreset(value) {
  const key = normalizeAuthorityAngle(value)
  return AUTHORITY_ANGLE_PRESETS.find((angle) => angle.key === key) || null
}

export function getAuthorityAngleLabel(value) {
  return getAuthorityAnglePreset(value)?.label || ''
}

export function getAuthorityAngleDefaultPillar(value) {
  return getAuthorityAnglePreset(value)?.defaultPillar || 'system-decision'
}

export function normalizeGithubMetadata(metadata = {}) {
  const github =
    metadata.github && typeof metadata.github === 'object'
      ? metadata.github
      : {}

  return {
    repo: cleanString(github.repo),
    kind: cleanString(github.kind),
    ref: cleanString(github.ref),
    externalId: cleanString(github.externalId),
  }
}

export function normalizeGithubRepo(value) {
  let repo = cleanString(value)
  repo = repo
    .replace(/^https?:\/\/github\.com\//i, '')
    .replace(/^git@github\.com:/i, '')
    .replace(/\.git$/i, '')
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase()
  const parts = repo.split('/').filter(Boolean)
  if (parts.length < 2) return ''
  const normalized = `${parts[0]}/${parts[1]}`
  return /^[a-z0-9_.-]+\/[a-z0-9_.-]+$/.test(normalized) ? normalized : ''
}

export function normalizeGithubRepoAllowlist(repos = []) {
  const seen = new Set()
  return (Array.isArray(repos) ? repos : [])
    .map(normalizeGithubRepo)
    .filter((repo) => {
      if (!repo || seen.has(repo)) return false
      seen.add(repo)
      return true
    })
}

export function normalizeGithubIngestCandidate(input = {}) {
  const repo = normalizeGithubRepo(input.repo)
  const kind = ['commit', 'pull_request', 'issue'].includes(input.kind)
    ? input.kind
    : ''
  const externalId = cleanString(input.externalId)
  const sourceUrl = cleanString(input.sourceUrl)
  const title = cleanString(input.title)

  return {
    id:
      cleanString(input.id) ||
      [repo, kind, externalId].filter(Boolean).join(':'),
    repo,
    kind,
    ref: cleanString(input.ref),
    externalId,
    title,
    evidence: cleanString(input.evidence),
    sourceUrl,
    capturedAt: cleanString(input.capturedAt),
  }
}

export function buildGithubCaptureFromCandidate(input = {}, options = {}) {
  const candidate = normalizeGithubIngestCandidate(input)
  const authorityAngle = normalizeAuthorityAngle(
    options.authorityAngle || input.authorityAngle
  )
  const pillar =
    cleanString(options.pillar || input.pillar) ||
    getAuthorityAngleDefaultPillar(authorityAngle)
  const contentType = inferContentTypeKey(
    options.contentType || 'linkedin-post'
  )
  const capturedAt =
    candidate.capturedAt ||
    cleanString(options.capturedAt) ||
    new Date().toISOString()

  return normalizeIdea({
    id: `github-${slugForId(`${candidate.repo}-${candidate.kind}-${candidate.externalId}`)}`,
    pillar,
    contentType,
    text: candidate.title || candidate.evidence || candidate.sourceUrl,
    metadata: {
      sourceType: 'github-activity',
      evidence: candidate.evidence,
      sourceUrl: candidate.sourceUrl,
      authorityAngle,
      projectIds: normalizeTruthLinkIds(options.projectIds),
      positionIds: normalizeTruthLinkIds(options.positionIds),
      github: {
        repo: candidate.repo,
        kind: candidate.kind,
        ref: candidate.ref,
        externalId: candidate.externalId,
      },
      capturedAt,
      status: 'active',
    },
    createdAt: capturedAt,
  })
}

export function normalizeCaptureMetadata(metadata = {}, fallback = {}) {
  const sourceType = CAPTURE_SOURCE_TYPE_KEYS.includes(metadata.sourceType)
    ? metadata.sourceType
    : 'raw-idea'
  const status = metadata.status === 'converted' ? 'converted' : 'active'
  const github = normalizeGithubMetadata(metadata)

  return {
    sourceType,
    evidence: cleanString(metadata.evidence),
    sourceUrl: cleanString(metadata.sourceUrl),
    authorityAngle: normalizeAuthorityAngle(metadata.authorityAngle),
    github,
    capturedAt:
      cleanString(metadata.capturedAt) ||
      cleanString(fallback.capturedAt) ||
      cleanString(fallback.createdAt),
    status,
    convertedAt: cleanString(metadata.convertedAt),
    convertedPostId: cleanString(metadata.convertedPostId),
  }
}

export function buildSourceWorkMetadata(idea) {
  const normalized = normalizeIdea(idea || {})
  const metadata = normalizeCaptureMetadata(normalized.metadata, {
    createdAt: normalized.createdAt,
  })
  const truthLinks = normalizeEntityTruthLinks(idea || {})

  return {
    ideaId: cleanString(normalized.id),
    sourceType: metadata.sourceType,
    evidence: metadata.evidence,
    sourceUrl: metadata.sourceUrl,
    authorityAngle: metadata.authorityAngle,
    authorityAngleLabel: getAuthorityAngleLabel(metadata.authorityAngle),
    github: metadata.github,
    capturedAt: metadata.capturedAt || cleanString(normalized.createdAt),
    text: cleanString(normalized.text),
    projectIds: truthLinks.projectIds,
    positionIds: truthLinks.positionIds,
  }
}

export function buildCaptureRoutingPayload(idea) {
  const normalized = normalizeIdea(idea || {})
  const sourceWork = buildSourceWorkMetadata(idea || {})
  const ctxLines = [
    sourceWork.evidence ? `Evidence: ${sourceWork.evidence}` : '',
    sourceWork.sourceUrl ? `Source URL: ${sourceWork.sourceUrl}` : '',
    sourceWork.authorityAngleLabel
      ? `Authority angle: ${sourceWork.authorityAngleLabel}`
      : '',
    sourceWork.github?.repo ? `GitHub repo: ${sourceWork.github.repo}` : '',
    sourceWork.github?.ref ? `GitHub ref: ${sourceWork.github.ref}` : '',
  ].filter(Boolean)

  return {
    mode: normalized.contentType === 'linkedin-carousel' ? 'carousel' : 'write',
    topic: cleanString(normalized.text),
    pillar: cleanString(normalized.pillar) || 'system-decision',
    contentType: inferContentTypeKey(normalized.contentType),
    ctx: ctxLines.join('\n'),
    sourceWork,
  }
}

export function buildConvertedCapturePatch(idea, postId, convertedAt) {
  const normalized = normalizeIdea(idea || {})
  const metadata = normalizeCaptureMetadata(normalized.metadata, {
    createdAt: normalized.createdAt,
  })

  return {
    metadata: {
      ...(normalized.metadata && typeof normalized.metadata === 'object'
        ? normalized.metadata
        : {}),
      ...metadata,
      status: 'converted',
      convertedAt: convertedAt || new Date().toISOString(),
      convertedPostId: cleanString(postId),
    },
  }
}

export function getPublishChecklistPreset(contentTypeKey) {
  const key = inferContentTypeKey(contentTypeKey)
  const items = CHECKLIST_PRESETS[key] || CHECKLIST_PRESETS['linkedin-post']

  return {
    version: 1,
    contentType: key,
    items: items.map(([id, label]) => ({ id, label, checked: false })),
  }
}

export function buildPublishChecklist(
  contentTypeKey,
  existingChecklist = null
) {
  const preset = getPublishChecklistPreset(contentTypeKey)
  const existingItems = Array.isArray(existingChecklist?.items)
    ? existingChecklist.items
    : []
  const checkedById = new Map(
    existingItems.map((item) => [item.id, item.checked === true])
  )

  return {
    ...preset,
    items: preset.items.map((item) => ({
      ...item,
      checked: checkedById.get(item.id) || false,
    })),
  }
}

export function isPublishChecklistComplete(checklist) {
  return (
    Array.isArray(checklist?.items) &&
    checklist.items.length > 0 &&
    checklist.items.every((item) => item.checked === true)
  )
}

export function buildDeliveryReceipt(post, input = {}) {
  const type = getContentType(post?.contentType)
  const method = normalizeDeliveryMethod(
    input.method,
    type.publishingMode === 'export-markdown' ? 'export' : 'manual'
  )

  return {
    channel: cleanString(input.channel) || getContentDisplayLabel(type),
    deliveredAt: cleanString(input.deliveredAt) || new Date().toISOString(),
    externalUrlOrRef: cleanString(input.externalUrlOrRef),
    method,
    notes: cleanString(input.notes),
    source: normalizeDeliverySource(input.source, method),
    relatedPublishAttemptId: cleanString(input.relatedPublishAttemptId),
  }
}

export function getDeliveryReceiptBadge(receipt) {
  if (!receipt || typeof receipt !== 'object') return null
  if (receipt.method === 'live-api') return 'Live API published'
  if (receipt.method === 'export') return 'Delivered by export'
  return 'Manually delivered'
}

export default {
  normalizeTruthLinkIds,
  normalizeEntityTruthLinks,
  CAPTURE_SOURCE_TYPES,
  CAPTURE_SOURCE_TYPE_KEYS,
  AUTHORITY_ANGLE_PRESETS,
  AUTHORITY_ANGLE_KEYS,
  PERFORMANCE_METRIC_FIELDS,
  getCaptureSourceTypeLabel,
  normalizeAuthorityAngle,
  getAuthorityAnglePreset,
  getAuthorityAngleLabel,
  getAuthorityAngleDefaultPillar,
  normalizeGithubMetadata,
  normalizeGithubRepo,
  normalizeGithubRepoAllowlist,
  normalizeGithubIngestCandidate,
  buildGithubCaptureFromCandidate,
  normalizeCaptureMetadata,
  normalizePerformanceMetric,
  normalizePerformanceFeedbackCheckIn,
  normalizePerformanceFeedback,
  buildPerformanceFeedbackCheckIn,
  appendPerformanceFeedbackCheckIn,
  hasPerformanceFeedback,
  getLatestPerformanceFeedbackCheckIn,
  buildSourceWorkMetadata,
  buildCaptureRoutingPayload,
  buildConvertedCapturePatch,
  getPublishChecklistPreset,
  buildPublishChecklist,
  isPublishChecklistComplete,
  buildDeliveryReceipt,
  getDeliveryReceiptBadge,
}
